import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

/** Prize coupons in pay. Codes are fixed; the tier is chosen per round. */
const PRIZE_OPTIONS = [
  { code: 'DRAWPRO1M', label: 'Pro 1개월' },
  { code: 'DRAWPRO3M', label: 'Pro 3개월' },
  { code: 'DRAWPRO6M', label: 'Pro 6개월' },
];

interface DrawWinner {
  user_id: string;
  rank: number;
  selection_method: 'RANDOM' | 'MANUAL';
  selection_note: string | null;
  awarded_at: string | null;
  award_error: string | null;
  notified_at: string | null;
}

interface Draw {
  id: string;
  period_key: string;
  prize_coupon_code: string;
  prize_description: string | null;
  entry_count: number;
  winner_count: number;
  seed: string;
  drawn_at: string | null;
  drawn_by: string | null;
  winners: DrawWinner[];
}

interface VerifyReport {
  period_key: string;
  matches: boolean;
  recorded: string[];
  recomputed: string[];
  entry_count_at_draw: number;
  entry_count_now: number;
}

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Running the record prize draw.
 *
 * Two ways to win, kept visibly separate. The random draw is reproducible from a published seed
 * and is what the user-facing copy calls 추첨. A manual pick is a judgement call — worth having
 * for a record that took real effort — but the seed cannot account for it, so it is stored and
 * displayed as a different thing rather than folded in.
 *
 * Drawing and awarding are separate buttons because they are separate risks: drawing is a
 * decision that must survive pay being unreachable, awarding is a network call that can fail
 * per winner and be retried.
 */
export default function DrawAdminPage() {
  const { token: accessToken } = useAuth();
  const [periodKey, setPeriodKey] = useState(currentPeriod());
  const [draw, setDraw] = useState<Draw | null>(null);
  const [verify, setVerify] = useState<VerifyReport | null>(null);
  const [prizeCode, setPrizeCode] = useState(PRIZE_OPTIONS[1].code);
  const [winnerCount, setWinnerCount] = useState(3);
  const [manualUserId, setManualUserId] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const xsrfCookie = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
    if (xsrfCookie) {
      headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
    }
    return headers;
  }, [accessToken]);

  const loadDraw = useCallback(async () => {
    setVerify(null);
    try {
      const response = await axios.get<{ draw: Draw }>(
        `/api/admin/reviews/draw/${encodeURIComponent(periodKey)}`,
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setDraw(response.data.draw);
    } catch {
      // 404 simply means this period has not been drawn yet, which is the normal starting state.
      setDraw(null);
    }
  }, [getAuthHeaders, periodKey]);

  useEffect(() => {
    void loadDraw();
  }, [loadDraw]);

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
    } catch (error) {
      const detail = axios.isAxiosError(error) ? error.response?.data?.error : null;
      setMessage(`${label} 실패: ${detail ?? String(error)}`);
    } finally {
      setBusy(false);
    }
  };

  const runDraw = () =>
    run('추첨', async () => {
      const prize = PRIZE_OPTIONS.find(option => option.code === prizeCode);
      const response = await axios.post<{ draw: Draw }>(
        '/api/admin/reviews/draw',
        {
          period_key: periodKey,
          prize_coupon_code: prizeCode,
          winner_count: winnerCount,
          prize_description: prize?.label,
        },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setDraw(response.data.draw);
      setMessage(`추첨 완료 — 응모 ${response.data.draw.entry_count}명 중 ${response.data.draw.winner_count}명`);
    });

  const addManual = () =>
    run('직접 선정', async () => {
      const response = await axios.post<{ draw: Draw }>(
        `/api/admin/reviews/draw/${encodeURIComponent(periodKey)}/winners`,
        { user_id: manualUserId.trim(), note: manualNote.trim() || undefined },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setDraw(response.data.draw);
      setManualUserId('');
      setManualNote('');
      setMessage('직접 선정 추가됨');
    });

  const award = () =>
    run('쿠폰 지급', async () => {
      const response = await axios.post<{ assigned: number; notified: number; failed?: string[] }>(
        `/api/admin/reviews/draw/${encodeURIComponent(periodKey)}/award`,
        {},
        { headers: getAuthHeaders(), withCredentials: true },
      );
      await loadDraw();
      const failed = response.data.failed?.length ?? 0;
      setMessage(
        `쿠폰 지급 ${response.data.assigned}명 · 알림 ${response.data.notified}명` +
          (failed ? ` · 실패 ${failed}명 (다시 실행하면 재시도됩니다)` : ''),
      );
    });

  const runVerify = () =>
    run('검증', async () => {
      const response = await axios.get<VerifyReport>(
        `/api/admin/reviews/draw/${encodeURIComponent(periodKey)}/verify`,
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setVerify(response.data);
    });

  const randomWinners = draw?.winners.filter(w => w.selection_method !== 'MANUAL') ?? [];
  const manualWinners = draw?.winners.filter(w => w.selection_method === 'MANUAL') ?? [];
  const pending = draw?.winners.filter(w => !w.awarded_at) ?? [];

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold">기록 추첨</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          해석에 기록을 남긴 사용자가 자동으로 응모됩니다. 응모는 기록 수와 무관하게 1인 1건입니다.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border p-4">
        <label className="text-sm">
          회차
          <input
            value={periodKey}
            onChange={event => setPeriodKey(event.target.value)}
            placeholder="YYYY-MM"
            className="ml-2 rounded border px-2 py-1"
          />
        </label>
        <Button variant="outline" onClick={() => void loadDraw()} disabled={busy}>
          불러오기
        </Button>
      </div>

      {message && <p className="rounded-lg border bg-muted/40 p-3 text-sm">{message}</p>}

      {!draw ? (
        <section className="space-y-3 rounded-lg border p-4">
          <h2 className="font-semibold">추첨 실행</h2>
          <p className="text-xs text-muted-foreground">
            한 회차는 한 번만 추첨할 수 있습니다. 이미 발표된 회차에 두 번째 당첨자를 만들면 되돌릴 수 없기
            때문입니다.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              상품
              <select
                value={prizeCode}
                onChange={event => setPrizeCode(event.target.value)}
                className="ml-2 rounded border px-2 py-1"
              >
                {PRIZE_OPTIONS.map(option => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              당첨 인원
              <input
                type="number"
                min={1}
                value={winnerCount}
                onChange={event => setWinnerCount(Number(event.target.value))}
                className="ml-2 w-20 rounded border px-2 py-1"
              />
            </label>
            <Button onClick={() => void runDraw()} disabled={busy}>
              추첨 실행
            </Button>
          </div>
        </section>
      ) : (
        <section className="space-y-4 rounded-lg border p-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-semibold">{draw.period_key}</h2>
            <Badge variant="secondary">응모 {draw.entry_count}명</Badge>
            <Badge variant="secondary">당첨 {draw.winner_count}명</Badge>
            {draw.prize_description && <Badge>{draw.prize_description}</Badge>}
            {pending.length > 0 && <Badge variant="destructive">미지급 {pending.length}명</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            시드 {draw.seed} · {draw.drawn_at?.slice(0, 19)} · {draw.drawn_by ?? '—'}
          </p>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void award()} disabled={busy || pending.length === 0}>
              쿠폰 지급 {pending.length > 0 ? `(${pending.length}명)` : ''}
            </Button>
            <Button variant="outline" onClick={() => void runVerify()} disabled={busy}>
              시드 검증
            </Button>
          </div>

          {verify && (
            <div className="rounded border p-3 text-sm">
              {/*
                Verification covers the random winners only — a hand-picked one cannot come out
                of a seed. A changed entrant count is not tampering either: records can be
                deleted on request, which legitimately changes the pool.
              */}
              <p className={verify.matches ? 'text-green-700' : 'text-red-700'}>
                {verify.matches ? '시드 재현 일치 — 무작위 당첨자가 그대로 재현됩니다' : '시드 재현 불일치'}
              </p>
              {verify.entry_count_at_draw !== verify.entry_count_now && (
                <p className="mt-1 text-xs text-muted-foreground">
                  응모자 {verify.entry_count_at_draw} → {verify.entry_count_now}명 (기록 삭제 요청 등으로 변동).
                  무작위 재현 결과에는 영향을 주지 않습니다.
                </p>
              )}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>순번</TableHead>
                <TableHead>사용자</TableHead>
                <TableHead>방식</TableHead>
                <TableHead>지급</TableHead>
                <TableHead>알림</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {draw.winners.map(winner => (
                <TableRow key={winner.user_id}>
                  <TableCell>{winner.rank}</TableCell>
                  <TableCell className="font-mono text-xs">{winner.user_id}</TableCell>
                  <TableCell>
                    {winner.selection_method === 'MANUAL' ? (
                      <span title={winner.selection_note ?? ''}>직접 선정</span>
                    ) : (
                      '무작위'
                    )}
                  </TableCell>
                  <TableCell>
                    {winner.awarded_at ? '완료' : winner.award_error ? `실패: ${winner.award_error}` : '대기'}
                  </TableCell>
                  <TableCell>{winner.notified_at ? '완료' : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="space-y-2 rounded border p-3">
            <h3 className="text-sm font-semibold">직접 선정 추가</h3>
            {/*
              A manual pick is stored and shown as a different thing from a drawn one. The note
              is published: a prize given by judgement owes the people who did not get it a
              stated basis.
            */}
            <p className="text-xs text-muted-foreground">
              무작위 추첨과 별도로 기록됩니다. 공개 보드에도 &quot;선정&quot;으로 구분 표시되며, 사유가 함께
              공개됩니다. 해당 회차에 기록을 남긴 사용자만 지정할 수 있습니다.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <input
                value={manualUserId}
                onChange={event => setManualUserId(event.target.value)}
                placeholder="user_id"
                className="w-72 rounded border px-2 py-1 font-mono text-xs"
              />
              <input
                value={manualNote}
                onChange={event => setManualNote(event.target.value)}
                placeholder="선정 사유 (공개됨)"
                className="w-64 rounded border px-2 py-1 text-xs"
              />
              <Button variant="outline" onClick={() => void addManual()} disabled={busy || !manualUserId.trim()}>
                추가
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            무작위 {randomWinners.length}명 · 직접 선정 {manualWinners.length}명
          </p>
        </section>
      )}
    </div>
  );
}
